/**
 * RichEditor — componente canônico de editor de texto (FASE 2 / C3).
 *
 * Auditoria de Coerência (2026-05-21) identificou que havia 2 editores
 * @mention paralelos (MentionTextarea e InternalNoteEditor) com APIs
 * divergentes. Memória feedback_design_system_rich_editor diz "TODOS
 * editores devem usar <RichEditor>" — mas componente não existia.
 *
 * Este componente consolida ambos via features opt-in:
 *   - mentions: autocomplete dropdown ao digitar @
 *   - emoji: shortcuts :smile: → 😄 etc no blur
 *   - markdown: hint visível com sintaxes básicas (progressive disclosure)
 *
 * APIs antigas migram pra wrappers finos:
 *   - MentionTextarea(value, onChange(text, mencoes), ...) → RichEditor
 *     wrapper que recompoñe mencoes via extractMentionedUserIds (mentions.ts)
 *   - InternalNoteEditor(value, onChange) → RichEditor com features ON
 *
 * `value` é o texto cru. Pro display formatado, callers continuam usando
 * `renderMarkdown` + `resolveMentions` separadamente.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';

// B1 (V2-COMERCIAL-AJUDA-UX-QA): o picker usa o lookup LEVE e NÃO-admin —
// `useUsers()` é admin-only e deixava o autocomplete VAZIO pra colaborador.
import { useUsersLookup } from '@/lib/admin-store';
import { Popover } from './Popover';
import {
  applyMention,
  expandEmojis,
  filterMentionCandidates,
  findMentionTrigger,
  type MentionUser,
} from '@/lib/mentions';

export interface RichEditorFeatures {
  /** @autocomplete dropdown (default false) */
  mentions?: boolean;
  /** :smile: → 😄 expansion no blur (default false) */
  emoji?: boolean;
  /** mostra hint de formatação markdown (default false) */
  markdownHint?: boolean;
}

export interface RichEditorProps {
  value: string;
  onChange: (next: string) => void;
  /** Opt-in features. Defaults = todas false (textarea simples). */
  features?: RichEditorFeatures;
  placeholder?: string;
  rows?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  /** ID HTML pra label htmlFor. */
  id?: string;
  /** Filtra users elegíveis a mencionar (default = ativos). */
  filterUsers?: (user: MentionUser) => boolean;
  /** Identifier base pra data-testid (default 'rich-editor'). */
  testid?: string;
}

/**
 * Hint mostrado abaixo do textarea quando markdownHint=true.
 * Progressive disclosure: visível default, oculto on-focus, reaparece on-blur
 * se value vazio (não atrapalha quem está digitando).
 */
const MARKDOWN_HINT =
  '**negrito** · *itálico* · `código` · [link](url)' +
  ' · `@` pra mencionar · `:smile:` pra emoji';

export function RichEditor({
  value,
  onChange,
  features = {},
  placeholder,
  rows = 3,
  autoFocus = false,
  disabled = false,
  id,
  filterUsers,
  testid = 'rich-editor',
}: RichEditorProps) {
  const mentionsOn = features.mentions ?? false;
  const emojiOn = features.emoji ?? false;
  const markdownHintOn = features.markdownHint ?? false;

  // Lookup já vem SÓ com usuários internos ativos — sem filtro de `ativo` aqui.
  const allUsers = useUsersLookup();
  const candidateUsers: MentionUser[] = useMemo(
    () =>
      allUsers
        .filter((u) => (filterUsers ? filterUsers({ id: u.id, nome: u.nome }) : true))
        .map((u) => ({ id: u.id, nome: u.nome })),
    [allUsers, filterUsers],
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [trigger, setTrigger] = useState<{ start: number; query: string } | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [focused, setFocused] = useState(false);

  const candidates = useMemo(
    () => (mentionsOn && trigger ? filterMentionCandidates(candidateUsers, trigger.query) : []),
    [mentionsOn, trigger, candidateUsers],
  );

  // Reset highlight quando candidates muda (evita index out of range)
  useEffect(() => {
    if (highlightIndex >= candidates.length) setHighlightIndex(0);
  }, [candidates.length, highlightIndex]);

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const text = e.target.value;
    const cursor = e.target.selectionStart ?? text.length;
    onChange(text);
    if (mentionsOn) {
      setTrigger(findMentionTrigger(text, cursor));
    }
  }

  function handleBlur() {
    setFocused(false);
    // Pequeno delay pra permitir click no dropdown registrar antes de fechar
    window.setTimeout(() => {
      setTrigger(null);
      // emoji expansion no blur
      if (emojiOn) {
        const expanded = expandEmojis(value);
        if (expanded !== value) onChange(expanded);
      }
    }, 150);
  }

  function handleFocus() {
    setFocused(true);
  }

  const selectCandidate = useCallback(
    (userId: string) => {
      if (!trigger || !textareaRef.current) return;
      const cursor = textareaRef.current.selectionStart ?? value.length;
      const { newText, newCursor } = applyMention(value, trigger.start, cursor, userId);
      onChange(newText);
      setTrigger(null);
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (ta) {
          ta.focus();
          ta.setSelectionRange(newCursor, newCursor);
        }
      });
    },
    [trigger, value, onChange],
  );

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!mentionsOn || !trigger || candidates.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % candidates.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + candidates.length) % candidates.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const c = candidates[highlightIndex];
      if (c) selectCandidate(c.id);
    } else if (e.key === 'Escape') {
      setTrigger(null);
    }
  }

  // Hint: visible quando markdownHintOn AND (não focado OR value vazio)
  const showHint = markdownHintOn && (!focused || !value);

  return (
    <div className="relative" data-testid={`${testid}-wrapper`}>
      <textarea
        ref={textareaRef}
        id={id}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        rows={rows}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        className="w-full border border-line dark:border-line-dark rounded-lg px-3 py-2 text-sm bg-paper-surface dark:bg-paper-dark-surface disabled:opacity-50"
        data-testid={testid}
      />
      {/* Mention autocomplete dropdown — CENSO-FLUTUANTE (05/08): era absolute
          dentro do wrapper e as zonas com overflow (CardTelaCheia, dialogs) o
          clipavam. Migrado pro <Popover> canônico (portal + flip/shift). */}
      <Popover
        aberto={Boolean(mentionsOn && trigger && candidates.length > 0)}
        gatilhoRef={textareaRef}
        onFechar={() => setTrigger(null)}
        className="max-w-sm"
      >
        <ul
          data-testid={`${testid}-mention-dropdown`}
          role="listbox"
          className="bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-lg shadow-lg overflow-hidden"
        >
          {candidates.map((c, idx) => (
            <li key={c.id}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectCandidate(c.id);
                }}
                data-testid={`${testid}-mention-option-${c.id}`}
                className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-paper-warm dark:hover:bg-paper-dark-warm ${
                  idx === highlightIndex ? 'bg-paper-warm dark:bg-paper-dark-warm' : ''
                }`}
              >
                @{c.nome}
              </button>
            </li>
          ))}
        </ul>
      </Popover>
      {/* Markdown hint progressive disclosure */}
      {showHint && (
        <p
          data-testid={`${testid}-hint`}
          className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1 italic"
        >
          {MARKDOWN_HINT}
        </p>
      )}
    </div>
  );
}
